import { useEffect, useState } from "react";
import ModulePage from "../components/ModulePage";
import { WorkOrders } from "../components/WorkOrders";
import { RagBadge } from "../components/Badges";
import { api, fmtDate } from "../lib/api";
import { AsrsSummary, AsrsPm, AsrsHistory } from "../components/Asrs";
import { AsrsTrends } from "../components/AsrsTrends";

export const AsrsPage = () => (
  <ModulePage eyebrow="ASRS / Crane Maintenance" title="ASRS & Cranes" module="asrs"
    description="Plant fault reporting and PPM for the automated storage cranes, infeeds, outfeeds and wrapper - same work order process, risk escalation and QR labels as Maintenance. Quickbase history syncs hourly until an admin switches over to this app."
    summary={<AsrsSummary />}
    tabs={[{ key: "asrs_faults", label: "Fault work orders", element: <WorkOrders category="ASRS" /> }, { key: "asrs_plant" }, { key: "asrs_ppm", label: "PPM schedule", element: <AsrsPm /> }, { key: "asrs_trends", label: "Fault trends", element: <AsrsTrends /> }, { key: "asrs_history", label: "Quickbase history", element: <AsrsHistory /> }]} />
);

export const MaintenancePage = () => (
  <ModulePage eyebrow="Module 01" title="Building Maintenance" module="maintenance"
    description="Reactive requests, defects and PPM work orders through Issue Raised to Close Out, with risk-based escalation."
    tabs={[{ key: "workorders", label: "Work orders", element: <WorkOrders /> }, { key: "building_asset" }, { key: "contractor" }]} />
);

const FIRE_TYPES = ["Fire Alarm Panel", "Extinguisher", "Emergency Lighting", "Fire Door", "Fire Exit", "Assembly Point"];
const RECORD_TYPES = ["Fire Drill", "Fire Training", "Fire Risk Assessment", "Service Certificate"];
const worst = (list) => (list.some((x) => x.rag === "red") ? "red" : list.some((x) => x.rag === "amber") ? "amber" : "green");

const FireSummary = () => {
  const [eq, setEq] = useState([]);
  const [rec, setRec] = useState([]);
  useEffect(() => {
    api.get("/assets", { params: { kind: "fire_equipment" } }).then((r) => setEq(r.data));
    api.get("/assets", { params: { kind: "fire_record" } }).then((r) => setRec(r.data));
  }, []);
  const tiles = [...FIRE_TYPES.map((t) => [t, eq.filter((e) => e.type === t)]), ...RECORD_TYPES.map((t) => [t, rec.filter((e) => e.record_type === t)])];
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="fire-summary">
      {tiles.map(([t, list]) => (
        <div key={t} className="panel p-3" data-testid={`fire-tile-${t.toLowerCase().replace(/ /g, "-")}`}>
          <p className="eyebrow text-[10px]">{t}</p>
          <div className="mt-2 flex items-center justify-between">
            <span className="font-mono text-2xl font-bold">{list.length}</span>
            <RagBadge status={list.length ? worst(list) : "amber"} label={list.length ? undefined : "None"} />
          </div>
          {list[0]?.next_due && <p className="mt-1 text-[10px] text-slate-500">Next: {fmtDate(list.map((x) => x.next_due).filter(Boolean).sort()[0])}</p>}
        </div>
      ))}
    </div>
  );
};

export const FirePage = () => (
  <ModulePage eyebrow="Module 02" title="Fire Safety Management" module="fire" summary={<FireSummary />}
    description="Alarm tests (weekly, monthly, six-monthly, annual), equipment, drills, training, FRAs, certificates, fault logs and remedial actions."
    templates={["fire_walkaround", "fire_alarm_weekly", "fire_alarm_monthly", "fire_alarm_6monthly", "fire_alarm_annual"]}
    tabs={[{ key: "fire_equipment" }, { key: "fire_record" }]} />
);

export const MachinesPage = () => (
  <ModulePage eyebrow="Module 05" title="Machine Safety (PUWER)" module="puwer"
    description="Machine register, daily guarding / e-stop / nip point checks, paper machine section checks and LOTO isolation. Start a check from a machine row."
    tabs={[{ key: "machine" }]} />
);

export const WelfarePage = () => (
  <ModulePage eyebrow="Module 06" title="Worker Welfare" module="welfare"
    description="Scheduled inspections for toilets, welfare areas and changing areas."
    templates={["welfare_toilets", "welfare_areas", "welfare_changing"]}
    tabs={[{ key: "welfare", label: "Recent inspections", element: <WelfareHint /> }]} />
);

const WelfareHint = () => <p className="panel p-6 text-sm text-slate-400">Start an inspection above. Completed inspections appear under "Inspection history"; failed items raise a work order automatically.</p>;

export const FirstAidPage = () => (
  <ModulePage eyebrow="Module 07" title="First Aid Management" module="first_aid"
    description="First aid kits, trauma kits and AEDs. Reminders are sent 90 and 30 days before expiry, with overdue escalation."
    tabs={[{ key: "first_aid" }]} />
);

export const LegionellaPage = () => (
  <ModulePage eyebrow="Water Safety (ACoP L8 / HSG274)" title="Legionella Management" module="legionella"
    description="Water system register, monthly sentinel and calorifier temperature checks with automatic pass/fail against limits, weekly flushing, quarterly shower cleaning, tank inspections, risk assessments and sampling records."
    templates={["legionella_flushing_weekly", "legionella_showers_quarterly"]}
    tabs={[{ key: "water_outlet" }, { key: "legionella_record" }]} />
);

export const LaddersPage = () => (
  <ModulePage eyebrow="Module 08" title="Ladder Management" module="ladders"
    description="Ladder register with six-monthly inspections, pre-use checks and green / red tagging. A failed inspection automatically red-tags the ladder."
    tabs={[{ key: "ladder" }]} />
);
